import { IEnvConfig } from '../envConfig/envConfig.types';
import { IAppConfig } from './appConfig.types';
import {
  getAppEnv,
  getAppPackageVersion,
  getApiHost,
  getGraphQlEndpoint,
  isProd,
  isMock,
  isLocalhost,
  getTargetBg,
  getAppVersionName,
  getQuantumEnvName,
  getTdcsDeviceType,
  getLoginUrl,
  getLoginParams,
  isTrueProd,
  getRestEndpoint,
} from './appConfig.utils';

/**
 * Create Application Config Object
 * @param envConfig the pipeline generated env config file
 */
export const createAppConfig = (envConfig: IEnvConfig): IAppConfig => {
  const appEnv = getAppEnv(envConfig);
  console.debug(`App running as '${appEnv}' env.`);

  const isProdEnv = isProd(appEnv);

  const appShortName = 'DXTR';
  const appFullVersion = getAppPackageVersion(envConfig);
  const { piHost, directHost } = getApiHost(appEnv);
  const auth = `${piHost}/auth/oauth/v2`;

  const piClientId = 'enterprise-dexter';

  const useDirectHost =
    envConfig.use_direct_host === 'true' ||
    import.meta.env.VITE_APP_USE_DIRECT_HOST === 'true';

  const graphql = getGraphQlEndpoint(
    useDirectHost ? directHost : piHost,
    useDirectHost
  );

  const restEndpoint = getRestEndpoint(
    useDirectHost ? directHost : piHost,
    useDirectHost
  );

  /**
   * App config stores anything static that's not expected to change at runtime or per environment.
   * If you're wanting to change settings at runtime (or per environment), use TDCS Config service.
   *
   * WARNING: Do not store any secrets (such as private API keys)!
   * For more info, see https://chalk.charter.com/display/ERSPM/FE+Configs.
   */
  return {
    isProd: isProdEnv,
    isTrueProd: isTrueProd(),
    isMock: isMock(appEnv),
    isLocal: isLocalhost(),

    app: {
      shortName: appShortName,
      displayName: 'Dexter',
      env: appEnv,
      version: appFullVersion,
    },

    api: {
      useDirectHost,
      auth,
      graphql,
      restEndpoint,
      piHost,
    },

    auth: {
      targetBg: getTargetBg(),
      loginUrl: getLoginUrl(auth, appEnv),
      loginParams: getLoginParams(piClientId, appEnv),
      logoutUrl: '/signed-out',
      piClientId,
      autoLogout: {
        timeoutMins: 55, // after 55 minutes, auto logout
        preTimeoutWarnMins: 5, // 5 mins before timeout, show the Idle Prompt Warning modal
      },
    },

    // Access Google API library
    google: {
      siteKey: '', // TODO: Request if needed
    },

    analytics: {
      quantum: {
        enabled: false,
        // https://prism.spectruminternal.com/documentation/helix/endpoints
        devEndpoint: '//collector.pi-charter.net/topics/sspp?bulk=true',
        prodEndpoint: '//collector.pi.spectrum.net/topics/sspp?bulk=true',

        // See MetaData Tab under https://prism.spectruminternal.com/<quantum.appName>@Web/quantum-resources/helix/requirements/decoder/2.40
        config: {
          domain: 'portals',
          /**
           * When ready to use Quantum, need to register your App with Quantum Team, they give you this AppName value
           * New App Registration Form: https://prism.spectrumtoolbox.com/documentation/helix
           */
          appName: '', // TODO: Need Quantum Issued AppName
          appVersion: getAppVersionName(envConfig),
          packageVersion: appFullVersion,
          appEnvName: getQuantumEnvName(appEnv),
          startSession: '', // TODO: Need Quantum Issued Session event, follow intake above
          requirementsVersion: '2.90', // ensure requirementsVersion up-to-date by checking https://prism.spectruminternal.com/<quantum.appName>@Web/quantum-resources/helix/requirements/versions
        },
      },

      adobe: {
        enabled: false,
        devEndpoint:
          '//assets.adobedtm.com/d5e6a435fd0f/61d188005020/launch-c755e44844e3-development.min.js',
        prodEndpoint:
          '//assets.adobedtm.com/d5e6a435fd0f/61d188005020/launch-f898116ba08a.min.js',
      },
    },

    /**
     * Access our TDCS Config Server. Disabled by default.
     * If want to use TDCS, see https://chalk.charter.com/display/SPECENT/FE+Configs#FEConfigs-TDCSConfigs
     */
    tdcs: {
      enabled: false, // TODO: If want to enable, need to Setup TDCS configs above
      apiKey: 'EPRIZE',
      host: piHost,
      config: {
        appVersion: '1.0.0',
        clientType: 'EPRIZE-PORTAL',
        deviceId: '123456789',
        osVersion: '1.0.0',
        deviceType: getTdcsDeviceType(appShortName, appEnv),
      },
      // Allows for local config files for local development, see repo readme.
      localConfig: import.meta.env.VITE_APP_TDCS_CONFIG || '',
    },

    datadog: {
      host: 'https://charter-blue.datadoghq.com',
      applicationId: isProdEnv
        ? 'c631084f-3167-4276-ab00-def13a1165fb'
        : '810d8583-3e10-466b-92e9-a7992edba8fa',
      env: isProdEnv ? undefined : appEnv,
    },
  };
};
